import { SkyButton, SkyCard, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useEffect, useRef, useState } from "react";
import { useSearch } from "wouter";
import {
  MCP_CONSENT_PATH,
  MCP_CONSENT_REQUEST_PARAM,
  type McpConsentDetails,
  type McpConsentRefusal,
} from "@shared/mcp/consent";
import { MCP_SCOPE_READ, MCP_SCOPE_WRITE } from "@shared/mcp/constants";
import { MCP_APPROVE_TABLE_THRESHOLD, MCP_WRITES_PER_HOUR } from "@shared/mcp/writeIntents";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useApproveMcpConsent, useDenyMcpConsent, useMcpConsentDetails } from "@/hooks/use-mcp";
import { failureMessage, isApiErrorCode } from "@/lib/api";
import { useAuth } from "@/lib/auth/AuthContext";
import { clearReauthHandoff, reauthReturnTo, stashReauthHandoff } from "@/lib/auth/reauthHandoff";
import {
  isMsalError,
  signInErrorCode,
  signInErrorView,
  type SignInErrorView,
} from "@/lib/auth/signInErrors";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { currentCorrelationId } from "@/lib/correlation";
import { logger } from "@/lib/logger";
import { leaveForClient } from "@/lib/mcp/leaveForClient";
import { formatInstant } from "@/lib/time/displayTime";

/**
 * The MCP consent page (ADR 0009 section 3, "Consent hardening"; release 8A read, release 8B
 * the separate write checkbox).
 *
 * `/mcp-oauth/authorize` sends the browser here with an opaque, signed reference to the request
 * it validated. The person is signed in (MSAL, any issuer) and in an instance, and that instance
 * is the one the grant will name. The page shows what the server says, never what the client
 * said about itself: the client's name from its `MCP_REDIRECT_URIS` entry, the redirect host
 * verbatim, the instance, and what the app may read. Approve and Deny are authenticated
 * `/api/v2` mutations whose answer is where the browser goes next.
 *
 * Release 8B: to a person holding a write permission on the instance (`offeredScopes` names
 * `skydata.write`), a second checkbox, UNTICKED by default, lets the app make changes too, with
 * a plain warning of what bounds them: the approve link in skyData, the hourly limit and the
 * audit trail. Credentials are never among the changes: the person types them in skyData.
 *
 * The server sends `frame-ancestors 'none'` and `X-Frame-Options: DENY` for this document
 * (`server/middleware/securityHeaders.ts`), so the page is never framed.
 */

/** What the app can read with `skydata.read`: metadata a person already sees in skyData. */
export const MCP_READ_ITEMS = [
  "Your connections, their status and when each one last refreshed",
  "Runs: when they ran, how they ended, their steps and short error summaries",
  "Schedules and what runs next",
  "dbt test and model results, as counts and statuses",
  "Data health summaries and incidents (skyMonitor)",
  "Who you are here: your name, email and role in this instance",
] as const;

/** What never crosses MCP (ADR 0009 section 7), said in the person's words. */
export const MCP_NEVER_ITEMS = [
  "Rows of your data: no previews, samples, exports or queries",
  "Credentials: no passwords, keys, tokens or connection secrets, in either direction. You always type them in skyData yourself",
] as const;

/** Said when the person is not offered write, or leaves it unticked. */
export const MCP_NO_CHANGES_ITEM = "Changes: it cannot create, change, run or delete anything";

/** What `skydata.write` lets the app do (ADR 0009 section 6), in the person's words. */
export const MCP_WRITE_ITEMS = [
  "Create a connection as a draft, without its credentials. You finish it in skyData",
  "Rename a connection, test it with its stored credentials, and choose its tables",
  "Enable or disable a connection (always waits for your approval in skyData)",
  "Create, change, pause and resume schedules",
  "Start runs, including a run now of a disabled schedule, retry runs and tables, and run dbt jobs",
] as const;

/** The plain warning beside the write checkbox: what bounds a change the person did not intend. */
export const MCP_WRITE_WARNING = `An AI app can be steered by text it reads, in skyData or elsewhere, into a change you did not ask for. Some changes wait for your approval on a link that opens in skyData: enabling or disabling a connection, anything that imports more than ${MCP_APPROVE_TABLE_THRESHOLD} tables, and every change after ${MCP_WRITES_PER_HOUR} in an hour. Smaller changes, such as pausing a schedule or starting a small run, happen without asking. Every change is recorded in the audit trail as yours, and it never deletes anything.`;

const PLATFORM_ADMIN_REFUSAL = {
  title: "Platform administrators cannot connect AI apps",
  message:
    "A platform administrator's access reaches every tenant, so it is never handed to an AI app. Sign in with a tenant account to connect one.",
};

const REFUSAL_COPY: Record<
  McpConsentRefusal,
  (details: McpConsentDetails) => {
    title: string;
    message: string;
  }
> = {
  platform_admin: () => PLATFORM_ADMIN_REFUSAL,
  mcp_off: (details) => ({
    title: "AI apps are turned off here",
    message: `An administrator has turned off AI apps for ${details.instance.name}. Ask them to turn it on, then connect again from ${details.clientName}.`,
  }),
  instance_locked: (details) => ({
    title: `${details.clientName} is already connected to another instance`,
    message: `${details.clientName} already reads ${details.existingGrant?.instanceName ?? "another instance"} for you. One connection reads one instance: switch to that instance to renew it, or add skyData to ${details.clientName} again as a second connector for ${details.instance.name}.`,
  }),
};

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <main
      id="main-content"
      className="mx-auto flex max-w-2xl flex-col gap-4 p-6"
      role="main"
      tabIndex={-1}
      data-testid="mcp-consent-page"
    >
      {children}
    </main>
  );
}

/** A bad or missing reference: nothing to approve, and the way back is the AI app. */
function InvalidRequest({ expired }: { expired: boolean }) {
  return (
    <PageFrame>
      <SkyText as="h1" size={600} weight="semibold" block>
        Connect an AI app
      </SkyText>
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        testId="mcp-consent-invalid"
        title={expired ? "This request has expired" : "There is no request to approve"}
        message="Go back to the AI app and connect skyData again. A request from it is valid for 15 minutes, in the browser tab that started it."
      />
    </PageFrame>
  );
}

/** PlatformAdmin with no instance chosen: refused before anything is asked of the server. */
export function McpConsentPlatformRefusal() {
  const copy = PLATFORM_ADMIN_REFUSAL;
  return (
    <PageFrame>
      <SkyText as="h1" size={600} weight="semibold" block>
        Connect an AI app
      </SkyText>
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        testId="mcp-consent-refusal"
        title={copy.title}
        message={copy.message}
      />
    </PageFrame>
  );
}

function List({ items, testId }: { items: readonly string[]; testId: string }) {
  return (
    <ul className="list-disc space-y-1 pl-5" data-testid={testId}>
      {items.map((item) => (
        <li key={item}>
          <SkyText size={300}>{item}</SkyText>
        </li>
      ))}
    </ul>
  );
}

/** What the app asks for, the scopes to tick and what is needed first, when nothing refuses it. */
function ConsentAsk({
  view,
  willWrite,
  writeOffered,
  readTicked,
  onReadTickedChange,
  writeTicked,
  onWriteTickedChange,
  busy,
  needsSignIn,
  minutes,
  reauthFailed,
  reauthRefusal,
}: {
  view: McpConsentDetails;
  willWrite: boolean;
  writeOffered: boolean;
  readTicked: boolean;
  onReadTickedChange: (checked: boolean) => void;
  writeTicked: boolean;
  onWriteTickedChange: (checked: boolean) => void;
  busy: boolean;
  needsSignIn: boolean;
  minutes: number;
  reauthFailed: boolean;
  /** Issue #2006: the sign-in MSAL refused, in `signInErrorView`'s words, never Microsoft's. */
  reauthRefusal: SignInErrorView | null;
}) {
  return (
    <>
      <section className="flex flex-col gap-2" data-testid="mcp-consent-can-read">
        <SkyText as="h2" size={400} weight="semibold">
          What {view.clientName} will be able to read
        </SkyText>
        <SkyText as="p" size={300} block>
          Metadata about {view.instance.name}, only what you can see in skyData yourself:
        </SkyText>
        <List items={MCP_READ_ITEMS} testId="mcp-consent-read-items" />
      </section>
      <section className="flex flex-col gap-2" data-testid="mcp-consent-never">
        <SkyText as="h2" size={400} weight="semibold">
          What it never gets
        </SkyText>
        <List
          items={willWrite ? MCP_NEVER_ITEMS : [...MCP_NEVER_ITEMS, MCP_NO_CHANGES_ITEM]}
          testId="mcp-consent-never-items"
        />
      </section>

      {view.existingGrant ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="mcp-consent-existing"
          title={`${view.clientName} is already connected to ${view.existingGrant.instanceName}`}
          message="Approving again renews that connection. It does not add a second one."
        />
      ) : null}

      <SingleCheckbox
        label="Read metadata in this instance (skydata.read)"
        description={
          writeOffered
            ? "Needed to connect."
            : "Needed to connect. Making changes needs a role that can change connections, schedules or runs here."
        }
        checked={readTicked}
        onChange={onReadTickedChange}
        disabled={busy}
        testId="mcp-consent-scope-read"
      />

      {writeOffered ? (
        <section className="flex flex-col gap-2" data-testid="mcp-consent-write">
          <SingleCheckbox
            label="Also make changes in this instance (skydata.write)"
            description="Off unless you tick it. You can connect with read only."
            checked={writeTicked}
            onChange={onWriteTickedChange}
            disabled={busy}
            testId="mcp-consent-scope-write"
          />
          <SkyText as="p" size={300} block>
            With this ticked, {view.clientName} can, as you:
          </SkyText>
          <List items={MCP_WRITE_ITEMS} testId="mcp-consent-write-items" />
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="mcp-consent-write-warning"
            title="Changes run as you"
            message={MCP_WRITE_WARNING}
          />
          {view.writeSwitchedOff ? (
            <SkyText as="p" size={200} color="muted" block testId="mcp-consent-write-switched-off">
              Your tenant has not allowed AI apps to make changes yet. If you tick this, the app can
              only read until a tenant administrator allows changes.
            </SkyText>
          ) : null}
        </section>
      ) : null}

      {needsSignIn ? (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          testId="mcp-consent-reauth"
          title="Confirm it is you first"
          message={`Connecting an app for the first time needs a sign-in from the last ${minutes} minutes. Signing in again may take you to your sign-in page. skyData brings you back here.`}
        />
      ) : null}
      {reauthFailed ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          testId="mcp-consent-reauth-error"
          title="We could not confirm your identity"
          message={
            reauthRefusal ? (
              <>
                {reauthRefusal.message} Nothing was connected.
                {reauthRefusal.code ? (
                  <SkyText size={200} color="muted" block testId="mcp-consent-reauth-code">
                    Code {reauthRefusal.code}
                  </SkyText>
                ) : null}
                <SkyText size={200} color="muted" block>
                  Reference id {currentCorrelationId()}
                </SkyText>
              </>
            ) : (
              "Try signing in again. Nothing was connected."
            )
          }
        />
      ) : null}
    </>
  );
}

export default function McpConsentPage() {
  const search = useSearch();
  const ref = new URLSearchParams(search).get(MCP_CONSENT_REQUEST_PARAM);
  const { selectedInstance } = useTenantInstance();
  const instanceId = selectedInstance?.id ?? null;
  const { account, reauthenticate } = useAuth();
  const accountEmail = account?.email ?? null;
  const zone = useDisplayTz();

  const details = useMcpConsentDetails(ref, instanceId);
  const approve = useApproveMcpConsent();
  const deny = useDenyMcpConsent();
  const decisionError = useInlineError();
  const [readTicked, setReadTicked] = useState(true);
  // Release 8B: never ticked for the person; they tick it.
  const [writeTicked, setWriteTicked] = useState(false);
  const [reauthNeeded, setReauthNeeded] = useState(false);
  const [reauthPending, setReauthPending] = useState(false);
  const [reauthFailed, setReauthFailed] = useState(false);
  const [reauthRefusal, setReauthRefusal] = useState<SignInErrorView | null>(null);
  const [leaving, setLeaving] = useState(false);
  const returnTo = `${MCP_CONSENT_PATH}?${search}`;

  // Back from a sign-in that MSAL returned straight here: the staged return is spent.
  const cleared = useRef(false);
  useEffect(() => {
    if (cleared.current) return;
    cleared.current = true;
    if (reauthReturnTo(accountEmail, instanceId) === returnTo) clearReauthHandoff();
  }, [accountEmail, instanceId, returnTo]);

  if (!ref) return <InvalidRequest expired={false} />;

  if (details.isError) {
    if (isApiErrorCode(details.error, "MCP_REQUEST_INVALID")) return <InvalidRequest expired />;
    return (
      <PageFrame>
        <SkyText as="h1" size={600} weight="semibold" block>
          Connect an AI app
        </SkyText>
        <SkyMessageBar
          intent="error"
          layout="multiline"
          testId="mcp-consent-load-error"
          title="We could not load this request"
          message={failureMessage(details.error, "Reload the page to try again.")}
        />
      </PageFrame>
    );
  }

  const view = details.data;
  if (!view) {
    return (
      <PageFrame>
        <SkyText size={300} color="muted" testId="mcp-consent-loading">
          Loading the request…
        </SkyText>
      </PageFrame>
    );
  }

  const needsSignIn = view.needsRecentSignIn || reauthNeeded;
  const busy = approve.isPending || deny.isPending || reauthPending || leaving;
  const refusal = view.refusal ? REFUSAL_COPY[view.refusal](view) : null;
  const writeOffered = view.offeredScopes.includes(MCP_SCOPE_WRITE);
  const willWrite = writeOffered && writeTicked;
  const minutes = Math.round(view.recentSignInMaxAgeSeconds / 60);

  function leave(redirectUrl: string) {
    setLeaving(true);
    leaveForClient(redirectUrl);
  }

  function onApprove() {
    if (!ref) return;
    decisionError.clear();
    approve.mutate(
      { request: ref, scopes: willWrite ? [MCP_SCOPE_READ, MCP_SCOPE_WRITE] : [MCP_SCOPE_READ] },
      {
        onSuccess: (result) => leave(result.redirectUrl),
        onError: (error) => {
          if (isApiErrorCode(error, "REAUTH_REQUIRED")) {
            setReauthNeeded(true);
            return;
          }
          if (isApiErrorCode(error, "MCP_REQUEST_INVALID")) void details.refetch();
          decisionError.show(error, "The connection was not approved.");
        },
      },
    );
  }

  function onDeny() {
    if (!ref) return;
    decisionError.clear();
    deny.mutate(
      { request: ref },
      {
        onSuccess: (result) => leave(result.redirectUrl),
        onError: (error) => decisionError.show(error, "We could not send your answer back."),
      },
    );
  }

  async function onSignInAgain() {
    setReauthFailed(false);
    setReauthRefusal(null);
    setReauthPending(true);
    // Held before the provider is asked: a redirecting provider never returns here.
    stashReauthHandoff({ returnTo, account: accountEmail, instanceId, draft: null, body: null });
    try {
      const outcome = await reauthenticate();
      if (outcome === "redirecting") return;
      clearReauthHandoff();
      if (outcome === "failed") {
        setReauthFailed(true);
        return;
      }
      setReauthNeeded(false);
      await details.refetch();
    } catch (cause) {
      clearReauthHandoff();
      // The code only (issue #2006): Microsoft's text names the directory and the resource.
      logger.warn("re-authentication for MCP consent could not be started", {
        "skydata.reason": signInErrorCode(cause) ?? "unknown",
      });
      if (isMsalError(cause)) setReauthRefusal(signInErrorView(cause));
      setReauthFailed(true);
    } finally {
      setReauthPending(false);
    }
  }

  return (
    <PageFrame>
      <div>
        <SkyText as="h1" size={600} weight="semibold" block testId="mcp-consent-title">
          Connect {view.clientName} to skyData
        </SkyText>
        <SkyText as="p" size={300} color="muted" block className="mt-1">
          {view.clientName} asks to read skyData as you, in one instance
          {writeOffered ? ", and, only if you allow it below, to make changes" : ""}. You can
          disconnect it at any time from Connected apps in your user menu.
        </SkyText>
      </div>

      <SkyCard className="flex flex-col gap-4 p-4">
        <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2">
          <dt>
            <SkyText size={300} weight="semibold">
              App
            </SkyText>
          </dt>
          <dd>
            <SkyText size={300} testId="mcp-consent-client">
              {view.clientName}
            </SkyText>
          </dd>
          <dt>
            <SkyText size={300} weight="semibold">
              Sends you back to
            </SkyText>
          </dt>
          <dd>
            <SkyText size={300} font="monospace" testId="mcp-consent-redirect-host">
              {view.redirectHost}
            </SkyText>
          </dd>
          <dt>
            <SkyText size={300} weight="semibold">
              Instance
            </SkyText>
          </dt>
          <dd>
            <SkyText size={300} testId="mcp-consent-instance">
              {view.instance.name}
            </SkyText>
            <SkyText size={200} color="muted" block>
              {view.instance.tenantDisplayName}
            </SkyText>
          </dd>
        </dl>
        <SkyText as="p" size={200} color="muted" block testId="mcp-consent-expires">
          This request works until {formatInstant(view.expiresAt, zone, "minutes", { named: true })}
          .
        </SkyText>
      </SkyCard>

      {refusal ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="mcp-consent-refusal"
          title={refusal.title}
          message={refusal.message}
        />
      ) : (
        <ConsentAsk
          view={view}
          willWrite={willWrite}
          writeOffered={writeOffered}
          readTicked={readTicked}
          onReadTickedChange={setReadTicked}
          writeTicked={writeTicked}
          onWriteTickedChange={setWriteTicked}
          busy={busy}
          needsSignIn={needsSignIn}
          minutes={minutes}
          reauthFailed={reauthFailed}
          reauthRefusal={reauthRefusal}
        />
      )}

      <InlineErrorBar
        error={decisionError.error}
        title="That did not work"
        onDismiss={decisionError.clear}
        testId="mcp-consent-error"
      />

      <div className="flex flex-wrap gap-2">
        {refusal ? null : needsSignIn ? (
          <SkyButton
            appearance="primary"
            testId="mcp-consent-sign-in-again"
            loading={reauthPending}
            disabled={busy}
            onClick={() => void onSignInAgain()}
          >
            Sign in again
          </SkyButton>
        ) : (
          <SkyButton
            appearance="primary"
            testId="mcp-consent-approve"
            loading={approve.isPending}
            disabled={busy || !readTicked}
            onClick={onApprove}
          >
            Approve
          </SkyButton>
        )}
        <SkyButton
          appearance="secondary"
          testId="mcp-consent-deny"
          loading={deny.isPending}
          disabled={busy}
          onClick={onDeny}
        >
          {refusal ? `Go back to ${view.clientName}` : "Deny"}
        </SkyButton>
      </div>
      <SkyText as="p" size={200} color="muted" block>
        Either way your browser goes back to {view.redirectHost}. Deny gives {view.clientName}{" "}
        nothing.
      </SkyText>
    </PageFrame>
  );
}
